<template>
  <div id="app">
    <!-- Demo Walkthrough Overlay -->
    <DemoWalkthrough />

    <!-- Demo Onboarding Modal (rendered globally so it persists across routes) -->
    <DemoOnboardingModal
      v-if="demoStore.showOnboarding && !walkthroughStore.isActive"
      :show="demoStore.showOnboarding && !walkthroughStore.isActive"
      :project-i-d="demoStore.demoTenantId || ''"
      @close="handleDemoOnboardingClose"
    />

    <!-- Top-left progress notification for when not on template editing screen -->
    <template-progress-top-notification />

    <!-- Join success toast -->
    <Teleport to="body">
      <transition name="toast-slide">
        <div v-if="showJoinToast" class="join-toast">
          <div class="join-toast-icon">✓</div>
          <div class="join-toast-text">
            <strong>You've been added to the team!</strong>
            <span>You now have access to this project.</span>
          </div>
        </div>
      </transition>
    </Teleport>

    <PullToRefresh
      :enabled="enablePullToRefresh"
      :onRefresh="handlePullRefresh"
    />

    <div class="route-view-wrapper" ref="routeWrapper">
      <div v-if="joinLinkProcessing" class="join-link-loading">
        <div class="join-link-loading-card">
          <div class="join-link-spinner"></div>
          <div class="join-link-title">Opening project...</div>
          <div class="join-link-subtitle">
            Verifying access to this workspace.
          </div>
        </div>
      </div>
      <router-view v-slot="{ Component, route }">
        <transition
          v-if="!joinLinkProcessing"
          :name="transitionName"
          :mode="transitionMode"
          :css="!isDragNav && !isTapSlide"
          @before-enter="beforeEnterHook"
          @enter="enterHook"
          @leave="leaveHook"
        >
          <keep-alive :max="4">
            <component :key="getRouteComponentKey(route)" :is="Component" />
          </keep-alive>
        </transition>
      </router-view>
    </div>

    <!-- Global No Access Modal (never show in demo mode) -->
    <NoAccessModal
      :show="accessStore.showNoAccess && !demoStore.isDemo"
      :message="accessStore.noAccessMessage"
      :projectId="accessStore.projectId"
      @close="accessStore.resetAccessModal()"
    />
  </div>
</template>

<script setup lang="ts">
import '@/styles.css';
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  onErrorCaptured,
  nextTick
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { authFlowInProgress } from '@/api/auth';
import DemoWalkthrough from '@/components/DemoWalkthrough.vue';
import DemoOnboardingModal from '@/components/DemoOnboardingModal.vue';
import TemplateProgressTopNotification from '@/components/TemplateProgressTopNotification.vue';
import NoAccessModal from '@/components/NoAccessModal.vue';
import PullToRefresh from '@/components/PullToRefresh.vue';
import { useLandingPersonalization } from '@/composables/useLandingPersonalization';
import { hapticNav, hapticCelebrate } from '@/utils/haptics';
import { swipeNavState } from '@/store/swipeNavStore';
import { useAccessStore } from '@/store/accessStore';
import { useScraperStore } from '@/store/scraperStore';
import { useDemoStore } from '@/store/demoStore';
import { useWalkthroughStore } from '@/store/walkthroughStore';
import { joinTenant } from '@/api/user';
import type { RouteLocationNormalizedLoaded } from 'vue-router';

const accessStore = useAccessStore();
const scraperStore = useScraperStore();
const demoStore = useDemoStore();
const walkthroughStore = useWalkthroughStore();
const route = useRoute();
const router = useRouter();
const joinLinkProcessing = ref(Boolean(route.query.j || route.query.ic));

const showJoinToast = ref(false);

function getRouteComponentKey(route: RouteLocationNormalizedLoaded) {
  if (route.name === 'forms') {
    return `forms:${String(route.params.projectID || '')}`;
  }
  return route.path;
}

// Demo onboarding close handler
async function handleDemoOnboardingClose() {
  demoStore.showOnboarding = false;
  if (demoStore.demoTenantId) {
    if (!walkthroughStore.completed) {
      walkthroughStore.start(demoStore.demoTenantId);
    }
  }
}
const isMobile = ref(false);

// Computed hook refs: Vue compiles simple identifiers as direct references
// (auto-unwrapping the ref), NOT wrapped in arrow functions like ternaries.
// This ensures Vue's Transition gets the actual function (with correct .length
// for done() handling) or undefined — never a wrapper that swallows the call.
const beforeEnterHook = computed(() =>
  isMobile.value ? handleTransBeforeEnter : undefined
);
const enterHook = computed(() =>
  isMobile.value ? handleTransEnter : undefined
);
const leaveHook = computed(() =>
  isMobile.value ? handleTransLeave : undefined
);

// ── Clash Royale-style horizontal slide transitions for mobile ──
// Desktop order: Dashboard, Customers, Templates, Forms, More
// Mobile order:  Forms, Templates, Dashboard, Customers, More (Dashboard centred)
// prettier-ignore
const DESKTOP_NAV_ROUTES = ['dashboard', 'customers', 'templates', 'forms', 'more']; // prettier-ignore
const MOBILE_NAV_ROUTES = [
  'forms',
  'templates',
  'dashboard',
  'customers',
  'more'
];

const NAV_ROUTES = computed(() =>
  isMobile.value ? MOBILE_NAV_ROUTES : DESKTOP_NAV_ROUTES
);

const NAV_INDEX = computed(() => {
  const routes = NAV_ROUTES.value;
  const moreIdx = routes.indexOf('more');
  const map: Record<string, number> = {};
  routes.forEach((r, i) => (map[r] = i));
  ['catalog', 'branding', 'organization', 'integrations', 'account'].forEach(
    (r) => (map[r] = moreIdx)
  );
  return map;
});

const transitionName = ref('fade');
// 'out-in' for fade (sequential), undefined for slides (simultaneous = see both pages)
// IMPORTANT: on mobile we NEVER use 'out-in' — keep-alive + out-in + rapid navigation
// causes stale DOM crashes (parentNode/nextSibling null). All mobile transitions are
// either simultaneous slides or instant (no transition name).
const transitionMode = ref<'out-in' | undefined>('out-in');
const routeWrapper = ref<HTMLElement | null>(null);

router.beforeEach((to, from) => {
  // If a snap-back navigation just completed, skip all transition logic
  // so the route change is instant (no visual animation).
  if (isSnapBack) {
    isSnapBack = false;
    transitionName.value = '';
    transitionMode.value = undefined;
    return;
  }

  // If a drag is in its release-animation phase and the user tapped bottom
  // nav or otherwise triggered a new navigation, force-finish it now so
  // the new navigation gets a clean transition.
  if (isDragNav.value && dragAnimPhase) {
    finishDragNav();
    // Fall through to set up a normal transition below
  }

  // During the drag-initiated push() we don't want to interfere
  if (isDragNav.value) return;

  if (!isMobile.value) {
    // Desktop: simultaneous swap — the leaving page is instantly hidden
    // so there's no gap where navbar/sidebar disappear.
    transitionName.value = 'desktop-swap';
    transitionMode.value = undefined;
    return;
  }

  // Mobile: never use out-in mode (causes keep-alive DOM crashes on rapid nav)
  const toIndex = NAV_INDEX.value[to.name as string] ?? -1;
  const fromIndex = NAV_INDEX.value[from.name as string] ?? -1;
  if (toIndex >= 0 && fromIndex >= 0 && toIndex !== fromIndex) {
    const distance = Math.abs(toIndex - fromIndex);

    // If we're mid-chain from a prior multi-step tap, just allow navigation
    if (tapSlideChaining) {
      // Ensure isTapSlide stays true for the transition hooks
      isTapSlide.value = true;
      transitionName.value = '';
      transitionMode.value = undefined;
      return;
    }

    tapSlideDir = toIndex > fromIndex ? 'left' : 'right';
    transitionName.value = '';
    transitionMode.value = undefined;

    if (distance > 1) {
      // Multi-step: chain rapidly through each intermediate tab
      const pid = (to.params.projectID || to.params.projectId) as string;
      const step = toIndex > fromIndex ? 1 : -1;
      tapSlideQueue = [];
      for (let i = fromIndex + step + step; ; i += step) {
        tapSlideQueue.push(`/${NAV_ROUTES.value[i]}/${pid}`);
        if (i === toIndex) break;
      }
      tapSlideDuration = Math.max(40, Math.round(100 / distance));
      tapSlideChaining = true;
      swipeNavState.tapSlideChaining = true;
      isTapSlide.value = true;
      // Redirect to the first intermediate tab
      return `/${NAV_ROUTES.value[fromIndex + step]}/${pid}`;
    } else {
      // Single step
      isTapSlide.value = true;
      tapSlideQueue = [];
      tapSlideDuration = 280;
    }
  } else {
    // Non-nav transitions (e.g. customers→user): instant swap, no transition
    // This avoids the out-in race condition entirely
    transitionName.value = '';
    transitionMode.value = undefined;
  }
});

// ── Safety net: catch DOM access errors from any residual transition races ──
onErrorCaptured((err) => {
  if (
    err instanceof TypeError &&
    (err.message?.includes('parentNode') ||
      err.message?.includes('nextSibling') ||
      err.message?.includes('insertBefore') ||
      err.message?.includes('subTree'))
  ) {
    console.warn('[App] Caught DOM error from rapid navigation, recovering...');
    transitionName.value = '';
    transitionMode.value = undefined;
    // Reset any stuck tap-slide state
    isTapSlide.value = false;
    tapSlideChaining = false;
    tapSlideQueue = [];
    swipeNavState.tapSlideChaining = false;
    return false; // prevent error from propagating
  }
});

// ── Tap-slide: JS-driven slide when tapping a tab on mobile ──
// Uses the same approach as drag nav (translate .app-container only) so
// that position:fixed navbar/sidebar stay stationary.
const isTapSlide = ref(false);
let tapSlideDir: 'left' | 'right' = 'left';
let tapSlideEnterRoot: HTMLElement | null = null;
let tapSlideLeaveRoot: HTMLElement | null = null;
let tapSlideHiddenEls: HTMLElement[] = [];
// Multi-step chaining: queue of route paths to visit after the current step
let tapSlideQueue: string[] = [];
let tapSlideDuration = 280;
let tapSlideChaining = false;

// ── Clash Royale drag-to-pan between nav pages ──
const isDragNav = ref(false);
// We translate only the .app-container (scrollable content) inside each page.
// The navbar and sidebar are position:fixed and identical on every page, so we
// hide the entering page's copies to avoid z-index fights during the drag.
let dragEnterContent: HTMLElement | null = null;
let dragLeaveContent: HTMLElement | null = null;
let dragEnterRoot: HTMLElement | null = null;
let dragLeaveRoot: HTMLElement | null = null;
let dragEnterDone: (() => void) | null = null;
let dragLeaveDone: (() => void) | null = null;
let dragHiddenEls: HTMLElement[] = [];
let dragHiddenFabs: HTMLElement[] = [];
let dragEnterAnim: Animation | null = null;
let dragLeaveAnim: Animation | null = null;
let dragDir: 'left' | 'right' = 'left';
let hasNavigated = false;
let touchStartX = 0;
let touchStartY = 0;
let directionLocked = false;
let isHorizontal = false;
// Generation counter: incremented each time a new drag starts. Stale WAAPI
// .finished callbacks compare their captured generation to the current one
// and no-op if they don't match, preventing cross-drag corruption.
let dragGeneration = 0;
// True between onSwipeTouchEnd (WAAPI starts) and finishDragNav.
// Lets beforeEach distinguish "drag-initiated push" (don't interfere)
// from "user tapped nav during release animation" (force-finish first).
let dragAnimPhase = false;
// True during snap-back release animation (user released before 50% progress).
let isSnapBackAnim = false;
// Flag for beforeEach: the next navigation is a snap-back, use no transition.
let isSnapBack = false;
// Track last touch X for calculating drag progress at release.
let lastTouchX = 0;
// Original route path when drag started, for navigating back on snap-back.
let dragFromPath = '';
// Scroll position of the original page's .app-container, saved before snap-back.
let snapBackScrollTop = 0;
// Velocity tracking: store recent touch positions/timestamps for momentum calculation.
// We track the last ~100ms of movement to compute a reliable release velocity.
let velocityTracker: { x: number; t: number }[] = [];
const VELOCITY_THRESHOLD = 0.4; // px/ms — flicks faster than this snap forward regardless of position

function getCurrentNavIndex(): number {
  const name = route.name as string;
  const idx = NAV_ROUTES.value.indexOf(name);
  if (idx >= 0) return idx;
  if (
    ['catalog', 'branding', 'organization', 'integrations', 'account'].includes(
      name
    )
  )
    return 4;
  return -1;
}

// ── JS transition hooks: take manual control during drag ──

// Helper: strip any stale inline styles left on a page root from a previous
// drag transition that may still live in the keep-alive cache.
function clearStaleTransitionStyles(rootEl: HTMLElement) {
  rootEl.style.cssText = '';
  const content = rootEl.querySelector('.app-container') as HTMLElement | null;
  if (content) {
    content.style.transition = '';
    content.style.transform = '';
    content.style.willChange = '';
  }
  const navbar = rootEl.querySelector('.navbar') as HTMLElement | null;
  const sidebar = rootEl.querySelector(
    '.sidebar-container'
  ) as HTMLElement | null;
  if (navbar) navbar.style.display = '';
  if (sidebar) sidebar.style.display = '';
}

// Tap-slide cleanup: called when each WAAPI animation finishes.
// Both enter and leave run in parallel; we track completion of both.
let tapSlideEnterDone = false;
let tapSlideLeaveDone = false;

function finishTapSlide(
  done: () => void,
  rootEl: HTMLElement,
  isEnter: boolean
) {
  if (isEnter) tapSlideEnterDone = true;
  else tapSlideLeaveDone = true;

  // Call Vue's done() immediately for this hook
  done();

  // Once both enter and leave are finished, clean up
  if (tapSlideEnterDone && tapSlideLeaveDone) {
    tapSlideEnterDone = false;
    tapSlideLeaveDone = false;

    // Restore hidden navbar/sidebar on entering page
    for (const el of tapSlideHiddenEls) el.style.display = '';
    tapSlideHiddenEls = [];

    // Clear inline styles
    if (tapSlideEnterRoot) {
      tapSlideEnterRoot.style.cssText = '';
      tapSlideEnterRoot = null;
    }
    if (tapSlideLeaveRoot) {
      tapSlideLeaveRoot.style.cssText = '';
      tapSlideLeaveRoot = null;
    }

    // If there are queued tabs, chain to the next one
    // Keep isTapSlide = true so the next transition hooks animate.
    if (tapSlideQueue.length > 0) {
      const next = tapSlideQueue.shift() as string;
      // Use the last step duration if this is the final tab
      if (tapSlideQueue.length === 0) {
        tapSlideDuration = 200;
      }
      router.push(next);
    } else {
      // All steps done — clear all flags
      isTapSlide.value = false;
      tapSlideChaining = false;
      swipeNavState.tapSlideChaining = false;
      routeWrapper.value?.classList.remove('is-transitioning');
    }
  }
}

function handleTransBeforeEnter(el: Element) {
  if (!isDragNav.value && !isTapSlide.value) return;
  // Lock the wrapper to fixed height + overflow:hidden during transitions
  routeWrapper.value?.classList.add('is-transitioning');
  // Hide the entering element BEFORE it's inserted into the DOM.
  // Without this, there's one frame between DOM insertion and @enter
  // where the page is visible at its default position, causing a flash.
  const rootEl = el as HTMLElement;
  rootEl.style.visibility = 'hidden';
}

function handleTransEnter(el: Element, done: () => void) {
  // ── Tap-slide: JS-driven slide for mobile tab taps ──
  if (isTapSlide.value && !isDragNav.value) {
    const rootEl = el as HTMLElement;
    tapSlideEnterRoot = rootEl;
    clearStaleTransitionStyles(rootEl);

    Object.assign(rootEl.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      zIndex: '1',
      overflow: 'hidden',
      visibility: 'visible'
    });

    // Hide entering page's navbar/sidebar (leaving page's copies stay visible)
    const navbar = rootEl.querySelector('.navbar') as HTMLElement | null;
    const sidebar = rootEl.querySelector(
      '.sidebar-container'
    ) as HTMLElement | null;
    tapSlideHiddenEls = [];
    if (navbar) {
      navbar.style.display = 'none';
      tapSlideHiddenEls.push(navbar);
    }
    if (sidebar) {
      sidebar.style.display = 'none';
      tapSlideHiddenEls.push(sidebar);
    }

    // Animate only .app-container from off-screen to center
    const content = rootEl.querySelector(
      '.app-container'
    ) as HTMLElement | null;
    if (content) {
      const startX = tapSlideDir === 'left' ? '100%' : '-100%';
      const anim = content.animate(
        [
          { transform: `translateX(${startX})` },
          { transform: 'translateX(0)' }
        ],
        {
          duration: tapSlideDuration,
          easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          fill: 'forwards'
        }
      );
      anim.finished.then(() => {
        anim.cancel();
        content.style.transform = '';
        finishTapSlide(done, rootEl, true);
      });
    } else {
      finishTapSlide(done, rootEl, true);
    }
    return;
  }

  if (!isDragNav.value) {
    // Mobile non-slide transition (e.g. customers→user detail): complete immediately
    done();
    return;
  }
  const rootEl = el as HTMLElement;
  dragEnterRoot = rootEl;
  dragEnterDone = done;

  // Clear any stale inline styles from a previous cached transition
  clearStaleTransitionStyles(rootEl);

  // The entering page root: absolute-positioned so both pages coexist.
  // overflow:hidden clips the translated .app-container to this root's bounds.
  // z-index 1 (lower) so the leaving page's sidebar stays visually on top.
  Object.assign(rootEl.style, {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    zIndex: '1',
    overflow: 'hidden',
    visibility: 'visible'
  });

  // Hide the entering page's navbar and sidebar — they're identical to the
  // leaving page's copies which stay visible and stationary.
  const navbar = rootEl.querySelector('.navbar') as HTMLElement | null;
  const sidebar = rootEl.querySelector(
    '.sidebar-container'
  ) as HTMLElement | null;
  dragHiddenEls = [];
  if (navbar) {
    navbar.style.display = 'none';
    dragHiddenEls.push(navbar);
  }
  if (sidebar) {
    sidebar.style.display = 'none';
    dragHiddenEls.push(sidebar);
  }

  // Find the .app-container inside and offset it off-screen
  const content = rootEl.querySelector('.app-container') as HTMLElement | null;
  dragEnterContent = content;
  if (content) {
    Object.assign(content.style, {
      transition: 'none',
      transform: dragDir === 'left' ? 'translateX(100%)' : 'translateX(-100%)',
      willChange: 'transform'
    });
  }
}

function handleTransLeave(el: Element, done: () => void) {
  // ── Tap-slide: JS-driven slide for mobile tab taps ──
  if (isTapSlide.value && !isDragNav.value) {
    const rootEl = el as HTMLElement;
    tapSlideLeaveRoot = rootEl;
    clearStaleTransitionStyles(rootEl);

    Object.assign(rootEl.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      zIndex: '2',
      overflow: 'hidden'
    });

    // Animate only .app-container from center to off-screen
    const content = rootEl.querySelector(
      '.app-container'
    ) as HTMLElement | null;
    if (content) {
      const endX = tapSlideDir === 'left' ? '-100%' : '100%';
      const anim = content.animate(
        [{ transform: 'translateX(0)' }, { transform: `translateX(${endX})` }],
        {
          duration: tapSlideDuration,
          easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          fill: 'forwards'
        }
      );
      anim.finished.then(() => {
        anim.cancel();
        content.style.transform = '';
        finishTapSlide(done, rootEl, false);
      });
    } else {
      finishTapSlide(done, rootEl, false);
    }
    return;
  }

  if (!isDragNav.value) {
    // Mobile non-slide transition: complete immediately
    done();
    return;
  }
  const rootEl = el as HTMLElement;
  dragLeaveRoot = rootEl;
  dragLeaveDone = done;

  // Clear any stale inline styles from a previous cached transition
  clearStaleTransitionStyles(rootEl);

  // The leaving page root: absolute-positioned, ON TOP of the entering page
  // so its fixed sidebar (bottom nav) stays above the incoming content.
  // overflow:hidden clips the translated .app-container.
  Object.assign(rootEl.style, {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    zIndex: '2',
    overflow: 'hidden'
  });

  // Find the .app-container inside and keep it in place
  const content = rootEl.querySelector('.app-container') as HTMLElement | null;
  dragLeaveContent = content;
  if (content) {
    Object.assign(content.style, {
      transition: 'none',
      transform: 'translateX(0)',
      willChange: 'transform'
    });
  }
}

function finishDragNav() {
  // Idempotent: safe to call multiple times (stale WAAPI callbacks, etc.)
  if (!isDragNav.value) return;

  // Suppress the indicator's CSS transition for one frame so it doesn't
  // animate a jump between the swipe position and the resting position.
  swipeNavState.noTransition = true;
  swipeNavState.active = false;
  swipeNavState.dominantIndex = -1;
  dragAnimPhase = false;

  // Cancel any running WAAPI animations
  dragEnterAnim?.cancel();
  dragLeaveAnim?.cancel();
  dragEnterAnim = null;
  dragLeaveAnim = null;

  // 1. Immediately hide the leaving page with display:none so it cannot
  //    flash back when we clear its position:absolute below.
  if (dragLeaveRoot) {
    dragLeaveRoot.style.display = 'none';
  }

  // 2. Restore hidden navbar/sidebar on the entering page
  for (const el of dragHiddenEls) el.style.display = '';
  dragHiddenEls = [];

  // 3. Restore teleported FABs
  for (const el of dragHiddenFabs) el.style.display = '';
  dragHiddenFabs = [];

  // 4. Clear ALL inline styles on both page roots and their content.
  //    The leaving root's display:none prevents any flash.
  if (dragEnterContent) {
    dragEnterContent.style.transition = '';
    dragEnterContent.style.transform = '';
    dragEnterContent.style.willChange = '';
  }
  if (dragLeaveContent) {
    dragLeaveContent.style.transition = '';
    dragLeaveContent.style.transform = '';
    dragLeaveContent.style.willChange = '';
  }
  if (dragEnterRoot) {
    dragEnterRoot.style.cssText = '';
  }
  // Clear leaving root styles EXCEPT display:none (we'll clear that after done())
  if (dragLeaveRoot) {
    dragLeaveRoot.style.cssText = 'display:none';
  }

  // 5. Tell Vue both transitions are done.
  //    enterDone() activates the entering component.
  //    leaveDone() deactivates the leaving component (keep-alive caches it,
  //    removing its DOM node from the document tree).
  if (dragEnterDone) dragEnterDone();
  if (dragLeaveDone) dragLeaveDone();

  // 6. After leaveDone(), Vue has moved the leaving element into its
  //    keep-alive cache (detached from document). Now clear the display:none
  //    so it doesn't persist when reactivated later.
  if (dragLeaveRoot) {
    dragLeaveRoot.style.cssText = '';
  }

  dragEnterContent = null;
  dragLeaveContent = null;
  dragEnterRoot = null;
  dragLeaveRoot = null;
  dragEnterDone = null;
  dragLeaveDone = null;
  isDragNav.value = false;
  hasNavigated = false;
  routeWrapper.value?.classList.remove('is-transitioning');

  // Clear the noTransition flag after two frames so the indicator
  // can CSS-transition normally again on subsequent tab taps.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      swipeNavState.noTransition = false;
    });
  });
}

// Cancel a drag and snap back to the original tab.
// Completes the Vue transition (done callbacks) then instantly navigates
// back to the original route so the user ends up where they started.
function cancelDragNav() {
  if (!isDragNav.value) return;

  swipeNavState.noTransition = true;
  swipeNavState.active = false;
  swipeNavState.dominantIndex = -1;
  dragAnimPhase = false;
  isSnapBackAnim = false;

  // Cancel any running WAAPI animations
  dragEnterAnim?.cancel();
  dragLeaveAnim?.cancel();
  dragEnterAnim = null;
  dragLeaveAnim = null;

  // Restore hidden navbar/sidebar on the entering page
  for (const el of dragHiddenEls) el.style.display = '';
  dragHiddenEls = [];

  // Restore teleported FABs
  for (const el of dragHiddenFabs) el.style.display = '';
  dragHiddenFabs = [];

  // Clear ALL inline styles on both page roots and their content
  if (dragEnterContent) {
    dragEnterContent.style.transition = '';
    dragEnterContent.style.transform = '';
    dragEnterContent.style.willChange = '';
  }
  if (dragLeaveContent) {
    dragLeaveContent.style.transition = '';
    dragLeaveContent.style.transform = '';
    dragLeaveContent.style.willChange = '';
  }
  if (dragLeaveRoot) {
    dragLeaveRoot.style.cssText = '';
  }
  if (dragEnterRoot) {
    dragEnterRoot.style.cssText = '';
  }

  // Save scroll position BEFORE Vue deactivates the leaving (original) page
  if (dragLeaveContent) {
    snapBackScrollTop = dragLeaveContent.scrollTop;
  }

  // Tell Vue both transitions are done.
  // This activates the entering (new tab) and deactivates the leaving (original tab).
  if (dragEnterDone) dragEnterDone();
  if (dragLeaveDone) dragLeaveDone();

  // Save the original path before clearing state
  const fromPath = dragFromPath;

  dragEnterContent = null;
  dragLeaveContent = null;
  dragEnterRoot = null;
  dragLeaveRoot = null;
  dragEnterDone = null;
  dragLeaveDone = null;
  isDragNav.value = false;
  hasNavigated = false;
  routeWrapper.value?.classList.remove('is-transitioning');

  // Clear noTransition after two frames
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      swipeNavState.noTransition = false;
    });
  });

  // Navigate back to the original tab with no animation.
  // isSnapBack tells beforeEach to use an instant transition (no slide).
  // router.replace() swaps the current history entry so the user's
  // forward navigation doesn't linger in the history stack.
  isSnapBack = true;
  router.replace(fromPath).then(() => {
    // Restore scroll position after Vue reactivates the original page
    nextTick(() => {
      const wrapper = routeWrapper.value;
      if (wrapper) {
        const container = wrapper.querySelector(
          '.app-container'
        ) as HTMLElement | null;
        if (container) container.scrollTop = snapBackScrollTop;
      }
    });
  });
}

// ── Touch event handlers ──
function onSwipeTouchStart(e: TouchEvent) {
  if (swipeNavState.disabled) return;
  if (!isMobile.value) return;
  if (getCurrentNavIndex() < 0) return;
  const target = e.target as HTMLElement;
  if (target.closest('.mobile-swipe-container')) return;

  // If a previous drag is still in-flight (e.g. release animation playing),
  // immediately finish it so we start this gesture from a clean state.
  if (isDragNav.value) {
    finishDragNav();
  }

  touchStartX = e.touches[0].clientX;
  touchStartY = e.touches[0].clientY;
  directionLocked = false;
  isHorizontal = false;
  hasNavigated = false;
  velocityTracker = [];
}

function onSwipeTouchMove(e: TouchEvent) {
  if (swipeNavState.disabled) return;
  if (!isMobile.value) return;
  if (directionLocked && !isHorizontal) return;
  const target = e.target as HTMLElement;
  if (target.closest('.mobile-swipe-container')) return;

  const currentIdx = getCurrentNavIndex();
  if (currentIdx < 0) return;

  const x = e.touches[0].clientX;
  const y = e.touches[0].clientY;
  const deltaX = x - touchStartX;
  const deltaY = y - touchStartY;
  lastTouchX = x;

  // Track recent touch positions for velocity calculation at release.
  // Keep only the last ~100ms of samples.
  const now = performance.now();
  velocityTracker.push({ x, t: now });
  while (velocityTracker.length > 1 && now - velocityTracker[0].t > 100) {
    velocityTracker.shift();
  }

  if (!directionLocked && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
    directionLocked = true;
    isHorizontal = Math.abs(deltaX) > Math.abs(deltaY);
  }
  if (!isHorizontal) return;

  // Once past threshold, trigger navigation (mounts next page via keep-alive)
  if (!hasNavigated && Math.abs(deltaX) > 25) {
    // Check edges
    if (deltaX < 0 && currentIdx >= NAV_ROUTES.value.length - 1) return;
    if (deltaX > 0 && currentIdx <= 0) return;

    // Safety: if somehow a previous drag is still active, finish it first
    if (isDragNav.value) {
      finishDragNav();
    }

    hasNavigated = true;
    isDragNav.value = true;
    dragGeneration++;
    dragDir = deltaX < 0 ? 'left' : 'right';
    dragFromPath = route.fullPath;
    transitionMode.value = undefined; // simultaneous

    // Hide teleported FABs (they live on <body>, outside the transition)
    dragHiddenFabs = [];
    document.querySelectorAll('.mobile-fab').forEach((fab) => {
      const el = fab as HTMLElement;
      el.style.display = 'none';
      dragHiddenFabs.push(el);
    });

    const pid = route.params.projectID as string;
    if (!pid) return;
    const nextIdx = deltaX < 0 ? currentIdx + 1 : currentIdx - 1;

    // Broadcast swipe progress so the nav indicator interpolates
    swipeNavState.active = true;
    swipeNavState.fromIndex = currentIdx;
    swipeNavState.toIndex = nextIdx;
    swipeNavState.progress = Math.min(
      1,
      Math.max(0, Math.abs(deltaX) / window.innerWidth)
    );
    // The dominant tab is whichever one takes up > 50% of the screen
    swipeNavState.dominantIndex = currentIdx;

    router.push(`/${NAV_ROUTES.value[nextIdx]}/${pid}`);
    return;
  }

  // Update nav indicator progress (even before root elements are ready)
  if (hasNavigated && swipeNavState.active) {
    const p = Math.min(1, Math.max(0, Math.abs(deltaX) / window.innerWidth));
    swipeNavState.progress = p;
    // Whichever tab occupies more screen is the "dominant" one
    swipeNavState.dominantIndex =
      p >= 0.5 ? swipeNavState.toIndex : swipeNavState.fromIndex;
  }

  // Translate only .app-container content areas to follow the finger
  if (hasNavigated && dragEnterContent && dragLeaveContent) {
    const screenW = window.innerWidth;
    if (dragDir === 'left') {
      const progress = Math.max(-1, Math.min(0, deltaX / screenW));
      dragLeaveContent.style.transform = `translateX(${progress * 100}%)`;
      dragEnterContent.style.transform = `translateX(${(1 + progress) * 100}%)`;
    } else {
      const progress = Math.max(0, Math.min(1, deltaX / screenW));
      dragLeaveContent.style.transform = `translateX(${progress * 100}%)`;
      dragEnterContent.style.transform = `translateX(${
        (-1 + progress) * 100
      }%)`;
    }
  }
}

function onSwipeTouchEnd() {
  if (swipeNavState.disabled) {
    directionLocked = false;
    isHorizontal = false;
    hasNavigated = false;
    swipeNavState.active = false;
    return;
  }
  if (!hasNavigated || !isDragNav.value) {
    directionLocked = false;
    isHorizontal = false;
    hasNavigated = false;
    swipeNavState.active = false;
    return;
  }

  // Determine snap direction using both position AND velocity (momentum).
  // This matches iOS / Android paging behavior:
  //   - Quick flick → snap in flick direction, even with small displacement
  //   - Slow drag → snap to whichever tab occupies > 50% of screen
  const screenW = window.innerWidth;
  const dragProgress = Math.abs(lastTouchX - touchStartX) / screenW;

  // Compute release velocity from the last ~100ms of touch samples
  let velocity = 0; // px/ms, positive = in drag direction (toward new tab)
  if (velocityTracker.length >= 2) {
    const first = velocityTracker[0];
    const last = velocityTracker[velocityTracker.length - 1];
    const dt = last.t - first.t;
    if (dt > 0) {
      // Raw velocity in px/ms. Sign: negative deltaX = left swipe.
      // Normalize so that velocity in the drag direction is positive.
      const rawV = (last.x - first.x) / dt;
      velocity = dragDir === 'left' ? -rawV : rawV;
    }
  }

  const shouldSnapForward =
    velocity > VELOCITY_THRESHOLD
      ? true
      : velocity < -VELOCITY_THRESHOLD
      ? false
      : dragProgress >= 0.5;

  // Mark that we're in the release-animation phase.
  dragAnimPhase = true;
  const gen = dragGeneration;
  const duration = 250;
  const easing = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';

  if (shouldSnapForward) {
    // ── Snap forward: complete navigation to new tab ──
    swipeNavState.active = false;

    if (dragLeaveContent) {
      const from = dragLeaveContent.style.transform || 'translateX(0)';
      const to = dragDir === 'left' ? 'translateX(-100%)' : 'translateX(100%)';
      dragLeaveAnim = dragLeaveContent.animate(
        [{ transform: from }, { transform: to }],
        { duration, easing, fill: 'forwards' }
      );
    }

    if (dragEnterContent) {
      const from = dragEnterContent.style.transform || 'translateX(0)';
      dragEnterAnim = dragEnterContent.animate(
        [{ transform: from }, { transform: 'translateX(0)' }],
        { duration, easing, fill: 'forwards' }
      );
    }

    const finish = () => {
      if (gen !== dragGeneration) return;
      finishDragNav();
    };
    if (dragEnterAnim) {
      dragEnterAnim.finished.then(finish).catch(finish);
    } else if (dragLeaveAnim) {
      dragLeaveAnim.finished.then(finish).catch(finish);
    } else {
      finishDragNav();
    }
  } else {
    // ── Snap back: return to original tab ──
    isSnapBackAnim = true;

    // Animate the nav indicator back to the original tab position.
    // Keep swipeNavState.active = true so the indicator interpolates smoothly
    // back to progress 0 (the fromIndex / original tab).
    const indicatorStart = swipeNavState.progress;
    const indicatorT0 = performance.now();
    const animateIndicatorBack = () => {
      if (!isSnapBackAnim) return; // interrupted
      const t = Math.min(1, (performance.now() - indicatorT0) / duration);
      swipeNavState.progress = indicatorStart * (1 - t);
      if (t < 1) requestAnimationFrame(animateIndicatorBack);
    };
    requestAnimationFrame(animateIndicatorBack);

    // Animate leaving content back to center (original position)
    if (dragLeaveContent) {
      const from = dragLeaveContent.style.transform || 'translateX(0)';
      dragLeaveAnim = dragLeaveContent.animate(
        [{ transform: from }, { transform: 'translateX(0)' }],
        { duration, easing, fill: 'forwards' }
      );
    }

    // Animate entering content back off-screen
    if (dragEnterContent) {
      const from = dragEnterContent.style.transform || 'translateX(0)';
      const to = dragDir === 'left' ? 'translateX(100%)' : 'translateX(-100%)';
      dragEnterAnim = dragEnterContent.animate(
        [{ transform: from }, { transform: to }],
        { duration, easing, fill: 'forwards' }
      );
    }

    const finish = () => {
      if (gen !== dragGeneration) return;
      // Set inline transforms to match final animation state before WAAPI cancel
      if (dragLeaveContent) dragLeaveContent.style.transform = 'translateX(0)';
      if (dragEnterContent) {
        dragEnterContent.style.transform =
          dragDir === 'left' ? 'translateX(100%)' : 'translateX(-100%)';
      }
      cancelDragNav();
    };
    if (dragEnterAnim) {
      dragEnterAnim.finished.then(finish).catch(finish);
    } else if (dragLeaveAnim) {
      dragLeaveAnim.finished.then(finish).catch(finish);
    } else {
      cancelDragNav();
    }
  }

  directionLocked = false;
  isHorizontal = false;
}

const enablePullToRefresh = computed(() => {
  if (!isMobile.value) return false;
  const path = route.path;
  const blockedPrefixes = [
    '/',
    '/auth',
    '/demo',
    '/shopify',
    '/privacy',
    '/terms',
    '/blog',
    '/home',
    '/info',
    '/slides',
    '/p1'
  ];
  return !blockedPrefixes.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
});

async function handlePullRefresh() {
  await new Promise<void>((resolve) => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve();
    }, 1200);

    const event = new CustomEvent('pull-refresh', {
      detail: {
        done: () => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          resolve();
        }
      }
    });

    window.dispatchEvent(event);
  });
}

useLandingPersonalization();

// Duolingo-style: subtle haptic tick on every mobile page transition
// + background prefetch when navigating to any project route
import { usePrefetchStore } from '@/store/prefetchStore';
const prefetchStore = usePrefetchStore();

router.afterEach((to) => {
  hapticNav();

  if (joinLinkProcessing.value || to.query.j || to.query.ic) {
    return;
  }

  // Prefetch all data when entering a project-scoped route
  const pid = to.params.projectID as string | undefined;
  if (pid && pid !== 'undefined' && pid !== 'default') {
    // If switching projects, clear stale cache
    if (
      prefetchStore.project?.projectId &&
      prefetchStore.project.projectId !== pid
    ) {
      prefetchStore.clearAll();
    }
    prefetchStore.prefetchAll(pid);
  }
});

// Also trigger prefetch for the initial page load (afterEach misses it)
{
  const pid = router.currentRoute.value.params.projectID as string | undefined;
  const hasJoinLink =
    Boolean(router.currentRoute.value.query.j) ||
    Boolean(router.currentRoute.value.query.ic);
  if (pid && pid !== 'undefined' && pid !== 'default' && !hasJoinLink) {
    prefetchStore.prefetchAll(pid);
  }
}

// Restore demo session on app startup (before auth listener kicks in)
demoStore.hydrateFromStorage();

// Listen for auth state changes — when user signs in with a stored join code,
// automatically join them to the tenant and show a success toast
onUnmounted(() => {
  document.removeEventListener('touchstart', onSwipeTouchStart);
  document.removeEventListener('touchmove', onSwipeTouchMove);
  document.removeEventListener('touchend', onSwipeTouchEnd);
});

onMounted(() => {
  const updateIsMobile = () => {
    isMobile.value = window.innerWidth <= 1000;
  };
  updateIsMobile();
  window.addEventListener('resize', updateIsMobile);

  // Swipe gesture listeners
  document.addEventListener('touchstart', onSwipeTouchStart, { passive: true });
  document.addEventListener('touchmove', onSwipeTouchMove, { passive: true });
  document.addEventListener('touchend', onSwipeTouchEnd, { passive: true });

  const auth = getAuth();
  let hasBeenSignedOut = false;

  onAuthStateChanged(auth, async (user) => {
    if (!user) {
      hasBeenSignedOut = true;
      return;
    }

    // Only process join after a sign-in transition (was signed out → now signed in)
    // or on first load if there's a join code
    const storedTenantId =
      scraperStore.newtenantid || localStorage.getItem('newtenantid');
    if (!storedTenantId) return;

    // If user just signed in (was previously signed out or this is a fresh page after auth redirect)
    if (hasBeenSignedOut || localStorage.getItem('newtenantid')) {
      // Skip if a signup/login flow is in progress — the auth flow already
      // handles tenant joining via the newtenantid extra passed to /v2/agent/auth
      if (authFlowInProgress) {
        console.log(
          '[App] Auth flow in progress - skipping joinTenant (handled via auth extras)'
        );
        return;
      }

      console.log(
        '[App] Auth state changed - user signed in with stored join code:',
        storedTenantId
      );

      // Check for stored invite code (from ?ic= param)
      const storedInviteCode = localStorage.getItem('invite_code') || undefined;

      try {
        const result = await joinTenant(storedTenantId, storedInviteCode);
        if (result.success) {
          // Clear the stored join code and invite code
          localStorage.removeItem('newtenantid');
          localStorage.removeItem('invite_code');
          scraperStore.newtenantid = null;

          if (!result.alreadyMember) {
            console.log('[App] Successfully joined tenant after sign-in!');

            // Clear the "no access" modal if it was showing
            accessStore.resetAccessModal();

            // Show the success toast
            hapticCelebrate();
            showJoinToast.value = true;
            setTimeout(() => {
              showJoinToast.value = false;
            }, 4000);

            // Reload after a brief delay so the toast is visible
            setTimeout(() => {
              window.location.reload();
            }, 1200);
          } else {
            console.log('[App] Already a member, no join needed');
            // Still clear the modal if showing
            accessStore.resetAccessModal();
          }
        } else if (result.code === 'agent_not_found') {
          console.log(
            '[App] Agent not found yet - backend will handle via auth extras'
          );
        } else {
          console.warn(
            '[App] Join tenant failed after sign-in:',
            result.message || result.code
          );
        }
      } catch (error) {
        console.error('[App] Failed to join tenant after sign-in:', error);
      }
    }
  });
});

// Capture join code from URL (?j=<tenantId>) and handle tenant joining
// Also handle ?ic=<code> for secure joining (project ID extracted from path)
// If user is logged in, immediately add them to the tenant
// If not logged in, store for use during auth flow
onMounted(async () => {
  const joinCode = route.query.j as string | undefined;
  const inviteCode = route.query.ic as string | undefined;

  // Extract project ID from path for ?ic= links (e.g. /dashboard/ABC123?ic=XYZ)
  let tenantId = joinCode;
  if (!tenantId && inviteCode) {
    const pathMatch = route.path.match(/\/(?:dashboard|organization)\/([^/]+)/);
    if (pathMatch) {
      tenantId = pathMatch[1];
    }
  }

  if (tenantId) {
    console.log('[App] Captured tenant ID:', tenantId);
    if (inviteCode) {
      console.log('[App] Captured invite code from URL:', inviteCode);
    }

    // Always store it for the login flow (in case they're not logged in yet)
    scraperStore.setNewTenantId(tenantId);
    if (inviteCode) {
      localStorage.setItem('invite_code', inviteCode);
    }

    // Remove the j and ic params from URL to clean it up (without full page reload)
    const newQuery = { ...route.query };
    delete newQuery.j;
    delete newQuery.ic;
    router.replace({ path: route.path, query: newQuery });

    // Check if user is already logged in - if so, join the tenant immediately
    const auth = getAuth();
    const user = auth.currentUser;

    if (user) {
      console.log(
        '[App] User is logged in, attempting to join tenant:',
        tenantId
      );
      try {
        const result = await joinTenant(tenantId, inviteCode);
        if (result.success) {
          // Clear the stored join code and invite code
          localStorage.removeItem('newtenantid');
          localStorage.removeItem('invite_code');
          scraperStore.newtenantid = null;

          if (result.alreadyMember) {
            console.log('[App] User already a member of tenant');
          } else {
            console.log(
              '[App] Successfully joined tenant! Showing toast and reloading...'
            );
            accessStore.resetAccessModal();
            showJoinToast.value = true;
            setTimeout(() => {
              window.location.reload();
            }, 1200);
            return;
          }
        } else if (result.code === 'agent_not_found') {
          console.log(
            '[App] Agent not found in DB, join code stored for auth flow'
          );
        } else {
          console.warn(
            '[App] Join tenant failed:',
            result.message || result.code
          );
        }
      } catch (error) {
        console.error('[App] Failed to join tenant:', error);
      } finally {
        joinLinkProcessing.value = false;
      }
    } else {
      console.log('[App] User not logged in, join code stored for auth flow');
      joinLinkProcessing.value = false;
    }
  } else {
    joinLinkProcessing.value = false;
  }
});
</script>

<style>
* {
  font-family: 'Poppins', sans-serif;
}

html,
body {
  height: 100%;
  padding: 0;
}

#app {
  min-width: 100%;
  min-height: 100%;
  max-width: 2000px;
  font-size: 16px;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #404040;
  display: flex;
  flex-direction: column;
}

a {
  color: inherit;
}
h1,
label {
  font-weight: 600;
}

input,
select,
textarea {
  padding: 10px;
  border-radius: 8px;
  border: none;
  box-sizing: border-box;
  resize: none;
  -webkit-box-shadow: -5px 4px 20px 0px rgba(0, 0, 0, 0.1);
  box-shadow: -5px 4px 20px 0px rgba(0, 0, 0, 0.1);
}

.text-gradient {
  background: linear-gradient(
    90deg,
    rgba(73, 177, 251, 1) 0%,
    rgba(0, 125, 216, 1) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

nav a {
  font-weight: bold;
  color: #404040;
}

nav a.router-link-exact-active {
  color: #1f90e2;
}

/* Desktop instant swap — simultaneous mode, leaving page hidden immediately */
.desktop-swap-enter-active {
  transition: none;
}
.desktop-swap-leave-active {
  display: none;
}

.join-link-loading {
  min-height: calc(100vh - 80px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
}

.join-link-loading-card {
  width: min(420px, 100%);
  background: white;
  border: 1px solid rgba(139, 92, 246, 0.16);
  border-radius: 20px;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.12);
  padding: 32px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.join-link-spinner {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 3px solid rgba(139, 92, 246, 0.16);
  border-top-color: #8b5cf6;
  animation: join-link-spin 0.75s linear infinite;
}

.join-link-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2937;
}

.join-link-subtitle {
  font-size: 0.95rem;
  color: #6b7280;
}

.fade-enter-active {
  animation: fade 0.25s;
}

.fade-leave-active {
  animation: fade 0.25s reverse;
}

@keyframes fade {
  from {
    opacity: 0%;
    z-index: 99;
  }

  to {
    opacity: 100%;
    z-index: 99;
  }
}

@keyframes join-link-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Clash Royale simultaneous slide transitions ── */
/* Both pages visible at once, sliding side-by-side */
.route-view-wrapper {
  position: relative;
  /* isolation creates a stacking context so child z-indexes don't leak beyond this wrapper */
  isolation: isolate;
}

@media (max-width: 1000px) {
  .route-view-wrapper {
    position: relative;
    overflow: hidden;
    isolation: isolate;
    /* Explicit height so wrapper doesn't collapse when both children are position:absolute */
    height: 100vh;
    height: 100dvh; /* dynamic viewport for mobile address bar */
  }
}

.slide-left-enter-active,
.slide-right-enter-active {
  transition: transform 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--color-background, #f9f9f9);
  will-change: transform;
  z-index: 2;
}

.slide-left-leave-active,
.slide-right-leave-active {
  transition: transform 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--color-background, #f9f9f9);
  will-change: transform;
  z-index: 1;
}

/* slide-left: swiping to next tab — new page enters from right, old exits to left */
.slide-left-enter-from {
  transform: translateX(100%);
}
.slide-left-leave-to {
  transform: translateX(-100%);
}

/* slide-right: swiping to previous tab — new page enters from left, old exits to right */
.slide-right-enter-from {
  transform: translateX(-100%);
}
.slide-right-leave-to {
  transform: translateX(100%);
}

@media screen and (max-width: 1000px) {
  html,
  body {
    padding: 0;
  }
}

/* Join success toast */
.join-toast {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100000;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-left: 4px solid #22c55e;
  border-radius: 12px;
  padding: 16px 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  max-width: 420px;
  width: calc(100% - 32px);
}

.join-toast-icon {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 50%;
  background: #22c55e;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
}

.join-toast-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
}

.join-toast-text strong {
  font-size: 15px;
  color: #1a1a1a;
}

.join-toast-text span {
  font-size: 13px;
  color: #666;
}

.toast-slide-enter-active {
  animation: toast-in 0.35s ease-out;
}

.toast-slide-leave-active {
  animation: toast-in 0.25s ease-in reverse;
}

@keyframes toast-in {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}
</style>
